import { useQueries } from '@tanstack/react-query'
import { Constants } from 'constants/constants'
import { QueryKeys } from 'constants/query-keys'
import { TIME } from 'utils/misc'
import { useAnalytics } from 'utils/analytics'
import { fetchMyTargetPerformanceGuestMetrics } from 'queries/myTarget-performance/performance-metrics'
import { MPMEnvType } from 'utils/env'
import { getGreenfieldLocationFilter } from 'utils/greenfield'
import { TableData } from 'utils/data'

interface MyTargetPerformanceGuestMetricsFetchProps {
    env: MPMEnvType
    accessToken: string
    location: string
    timeDimensions: string[]
    enabled?: boolean
}

interface UseMyTargetPerformanceGuestMetricsResult {
    dataByTimeDimension: Record<string, TableData | undefined>
    isLoading: boolean
    isError: boolean
    errors: unknown[]
    results: ReturnType<typeof useQueries>
}

export function useMyTargetPerformanceGuestMetrics({
    env,
    accessToken,
    location,
    timeDimensions,
    enabled = true,
}: MyTargetPerformanceGuestMetricsFetchProps): UseMyTargetPerformanceGuestMetricsResult {
    const { trackEvent } = useAnalytics()
    const { tenMinutes } = TIME
    const greenfieldCardNumber =
        Constants.GREENFIELD_CARDS.MY_TARGET_PERFORMANCE.GUEST_METRICS

    // Create a query for each time dimension
    const queries = timeDimensions.map((timeDimension) => {
        const filters = {
            ...getGreenfieldLocationFilter(location),
            time_dimension: timeDimension,
        }

        return {
            queryKey: [
                QueryKeys.MY_TARGET_PERFORMANCE_GUEST_METRICS,
                location,
                timeDimension,
            ],
            queryFn: () =>
                fetchMyTargetPerformanceGuestMetrics({
                    env,
                    accessToken,
                    dynamicSubPath: greenfieldCardNumber,
                    method: 'GET',
                    filters,
                    trackEvent,
                }),
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
            enabled: enabled && timeDimensions.length > 0,
        }
    })

    const results = useQueries({ queries })

    // Combine results into a map keyed by time dimension
    const dataByTimeDimension = results.reduce(
        (acc, result, index) => {
            const timeDimension = timeDimensions[index]
            acc[timeDimension] = result.data
            return acc
        },
        {} as Record<string, TableData | undefined>,
    )

    const isLoading = results.some((result) => result.isLoading)
    const isError = results.some((result) => result.isError)
    const errors = results
        .filter((result) => result.error)
        .map((result) => result.error)

    return {
        dataByTimeDimension,
        isLoading,
        isError,
        errors,
        results,
    }
}
